import { forwardRef, useImperativeHandle, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { NumberInput, TextInput, TextareaInput } from 'components/FormControl';

import { yupResolver } from '@hookform/resolvers/yup';
import { TOAST } from 'constant';
import { toast } from 'layout';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';

import { domesticPaperSchema } from 'features/ScientificProfile/validation';

const formOptions = { resolver: yupResolver(domesticPaperSchema) };

const DomesticPaperDialog = forwardRef((props, ref) => {
  const { t } = useTranslation();

  const [domesticPaperControl, setDomesticPaperControl] = useState();
  const [visible, setVisible] = useState(false);

  const {
    control,
    watch,
    setValue,
    reset,
    trigger,
    formState: { errors, dirtyFields },
  } = useForm({ ...formOptions, mode: 'onChange' });

  // #region Event

  useImperativeHandle(
    ref,
    () => ({
      open: (_domesticPaperControl) => {
        setDomesticPaperControl(_domesticPaperControl);
        setVisible(true);
        if (_domesticPaperControl.type === 'INSERT') {
          reset({});
        } else if (_domesticPaperControl.type === 'UPDATE') {
          reset(_domesticPaperControl.value);
        }
      },
    }),
    []
  );

  const handleAddDomesticPaper = async () => {
    setValue('paper.publicationType', 'DOMESTIC');

    const isValidTrigger = await trigger();
    if (!isValidTrigger) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
      return;
    }

    const data = watch();

    const { insert, update, index, type } = domesticPaperControl;

    if (type === 'INSERT' && insert) {
      insert(index, data);
    } else if (type === 'UPDATE' && update) {
      update(index, data);
    }
    reset({});
    setVisible(false);
  };

  // #endregion Event

  return (
    <>
      <Dialog
        header={
          domesticPaperControl?.type === 'INSERT'
            ? t('scientificProfile.scientificWork.paper.domestic.add')
            : t('scientificProfile.scientificWork.paper.domestic.update')
        }
        visible={visible}
        onHide={() => {
          setVisible(false);
        }}
        draggable={false}
        className="w-full sm:w-7 lg:w-6"
      >
        <div className="grid p-fluid">
          <div className="col-12">
            <TextInput
              label={t('scientificProfile.scientificWork.paper.title')}
              name="paper.title"
              control={control}
              errors={errors}
              isRequired
            />
          </div>

          <div className="col-6">
            <TextareaInput
              label={t('scientificProfile.scientificWork.paper.journal')}
              name="paper.journal"
              control={control}
              errors={errors}
              isRequired
            />
          </div>
          <div className="col-6">
            <TextInput
              label={t('scientificProfile.scientificWork.paper.journalAbbrev')}
              name="paper.journalAbbrev"
              control={control}
              errors={errors}
              isRequired
            />
          </div>

          <div className="col-6">
            <TextareaInput
              label={t('scientificProfile.scientificWork.paper.pages')}
              name="paper.pages"
              control={control}
              errors={errors}
              isRequired
            />
          </div>
          <div className="col-6">
            <TextareaInput
              label={t('scientificProfile.scientificWork.paper.authors')}
              name="paper.authors"
              control={control}
              errors={errors}
              isRequired
            />
          </div>

          <div className="col-6">
            <NumberInput
              label={t('scientificProfile.scientificWork.paper.year')}
              name="paper.year"
              control={control}
              errors={errors}
              min={0}
              isRequired
            />
          </div>
          <div className="col-6">
            <TextInput
              label={t('scientificProfile.scientificWork.paper.projectCode')}
              name="paper.projectCode"
              control={control}
              errors={errors}
            />
          </div>

          <div className="col-6">
            <TextInput
              label={t('scientificProfile.scientificWork.paper.issn')}
              name="paper.issn"
              control={control}
              errors={errors}
            />
          </div>

          <div className="col-12">
            <TextareaInput
              label={t('scientificProfile.scientificWork.paper.note')}
              name="note"
              control={control}
              errors={errors}
            />
          </div>
        </div>

        <div className="flex flex-wrap justify-content-end mt-4">
          <Button
            label={t('formLayout.action.cancel')}
            severity="danger"
            onClick={() => {
              setVisible(false);
            }}
            className="mr-1 w-8rem"
          />
          <Button
            label={
              domesticPaperControl?.type === 'INSERT'
                ? t('formLayout.action.add')
                : t('formLayout.action.update')
            }
            severity="info"
            type="submit"
            className="w-8rem"
            onClick={handleAddDomesticPaper}
            disabled={!Object.keys(dirtyFields).length}
          />
        </div>
      </Dialog>

      {/* {isCreatePaperLoading && <Loading />} */}
    </>
  );
});

DomesticPaperDialog.propTypes = {};

DomesticPaperDialog.defaultProps = {};

export default DomesticPaperDialog;
